import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "@/i18n";

const CONSENT_KEY = "e2law-consent-v2";
const LEGACY_KEY = "e2law-consent";

const readConsent = () => {
  try {
    const v2 = localStorage.getItem(CONSENT_KEY);
    if (v2) return JSON.parse(v2);
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) return { analytics: legacy === "accepted" };
  } catch {}
  return null;
};

export default function CookieBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const stored = readConsent();
    if (!localStorage.getItem(CONSENT_KEY) && !stored) setVisible(true);
    else if (!localStorage.getItem(CONSENT_KEY) && stored) {
      // Migration silencieuse de l'ancien format
      localStorage.setItem(CONSENT_KEY, JSON.stringify(stored));
    }
    const onReset = () => {
      const cur = readConsent();
      setAnalytics(cur?.analytics === true);
      setCustomizing(true);
      setVisible(true);
    };
    window.addEventListener("e2law-consent-reset", onReset);
    return () => window.removeEventListener("e2law-consent-reset", onReset);
  }, []);

  const choose = (value) => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(value));
    window.dispatchEvent(new CustomEvent("e2law-consent", { detail: value }));
    setVisible(false);
    setCustomizing(false);
  };

  const Toggle = ({ checked, disabled, onChange, testid }) => (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange && onChange(!checked)}
      data-testid={testid}
      className={`relative w-11 h-6 shrink-0 transition-colors duration-300 ${
        checked ? "bg-gold" : "bg-white/10"
      } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`absolute top-0.5 w-5 h-5 bg-[#FAF8F5] transition-transform duration-300 ${
          checked ? "translate-x-[22px]" : "translate-x-0.5"
        }`}
      />
    </button>
  );

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-0 left-0 right-0 z-[60] px-4 pb-4 sm:px-6 sm:pb-6"
          data-testid="cookie-banner"
          role="dialog"
          aria-live="polite"
        >
          <div className="max-w-3xl mx-auto glass border border-gold/20 px-6 py-5 sm:px-8 sm:py-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-5">
              <p className="text-xs sm:text-sm text-[#A0A5B1] leading-relaxed flex-1">
                {t("cookies.text")}{" "}
                <Link to="/vie-privee" className="text-gold underline underline-offset-4 hover:text-gold-light" data-testid="cookie-more-link">
                  {t("cookies.more")}
                </Link>
              </p>
              <div className="flex items-center gap-3 shrink-0 flex-wrap">
                <button
                  onClick={() => choose({ analytics: false })}
                  data-testid="cookie-refuse-button"
                  className="border border-white/20 text-[#A0A5B1] text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 hover:border-gold/50 hover:text-gold transition-colors duration-300"
                >
                  {t("cookies.refuse")}
                </button>
                <button
                  onClick={() => setCustomizing(!customizing)}
                  data-testid="cookie-customize-button"
                  className={`border text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 transition-colors duration-300 ${
                    customizing ? "border-gold text-gold" : "border-white/20 text-[#A0A5B1] hover:border-gold/50 hover:text-gold"
                  }`}
                >
                  {t("cookies.customize")}
                </button>
                <button
                  onClick={() => choose({ analytics: true })}
                  data-testid="cookie-accept-button"
                  className="bg-gold text-ink text-[11px] uppercase tracking-[0.2em] font-medium px-5 py-2.5 hover:bg-gold-light transition-colors duration-300"
                >
                  {t("cookies.accept")}
                </button>
              </div>
            </div>

            {customizing && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden"
                data-testid="cookie-customize-panel"
              >
                <div className="mt-6 pt-6 border-t border-white/10 space-y-5">
                  <div className="flex items-start gap-4">
                    <Toggle checked={true} disabled={true} testid="cookie-toggle-necessary" />
                    <div>
                      <p className="text-xs uppercase tracking-[0.15em] text-[#FAF8F5]">{t("cookies.catNecT")}</p>
                      <p className="mt-1 text-xs text-[#9BA1B0] leading-relaxed">{t("cookies.catNecD")}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Toggle checked={analytics} onChange={setAnalytics} testid="cookie-toggle-analytics" />
                    <div>
                      <p className="text-xs uppercase tracking-[0.15em] text-[#FAF8F5]">{t("cookies.catAnaT")}</p>
                      <p className="mt-1 text-xs text-[#9BA1B0] leading-relaxed">{t("cookies.catAnaD")}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => choose({ analytics })}
                    data-testid="cookie-save-button"
                    className="bg-gold text-ink text-[11px] uppercase tracking-[0.2em] font-medium px-5 py-2.5 hover:bg-gold-light transition-colors duration-300"
                  >
                    {t("cookies.save")}
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
